import React from 'react';
import { ArrowRight, Target, Users, TrendingUp, Heart } from 'lucide-react';
import { strategicVisionData } from '../data/mock';
import { Button } from './ui/button';

const StrategicVisionSection = () => {
  const icons = [Target, Users, TrendingUp, Heart];
  const colors = [
    { bg: 'bg-teal-100', text: 'text-teal-600', border: 'border-teal-200' },
    { bg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200' },
    { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200' },
    { bg: 'bg-rose-100', text: 'text-rose-600', border: 'border-rose-200' }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-white to-slate-50 relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-teal-100/30 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 left-0 w-96 h-96 bg-amber-100/30 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Column - Content */}
          <div className="order-2 lg:order-1">
            <span className="inline-block px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold mb-6">
              2026-2029 Strategic Plan
            </span>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800 mb-6 leading-tight">
              {strategicVisionData.title}
            </h2>

            <p className="text-slate-600 text-lg leading-relaxed mb-10">
              {strategicVisionData.description}
            </p>

            {/* Goals */}
            <div className="space-y-4 mb-10">
              {strategicVisionData.goals.map((goal, index) => {
                const Icon = icons[index];
                const color = colors[index];
                return (
                  <div
                    key={goal.id}
                    className={`flex items-start gap-4 p-5 rounded-2xl bg-white border ${color.border} shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-x-1 group`}
                  >
                    <div className={`w-12 h-12 ${color.bg} rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className={`w-6 h-6 ${color.text}`} />
                    </div>
                    <div>
                      <h4 className="font-bold text-slate-800 mb-1">
                        {goal.title}
                      </h4>
                      <p className="text-slate-500">
                        {goal.description}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <Button
              onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
              size="lg"
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 group"
            >
              Join Our Mission
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </Button>
          </div>

          {/* Right Column - Image */}
          <div className="order-1 lg:order-2 relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src={strategicVisionData.image}
                alt="Children learning together"
                className="w-full h-96 lg:h-[550px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent" />
            </div>

            {/* Stats Card */}
            <div className="absolute -bottom-6 -left-6 lg:left-8 bg-white rounded-2xl shadow-xl p-6 border border-slate-100">
              <p className="text-4xl font-bold bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent mb-1">
                2026-2029
              </p>
              <p className="text-slate-600 font-medium">Strategic Plan</p>
              <p className="text-sm text-slate-500 mt-2">Intentional & Forward-Looking</p>
            </div>

            {/* Decorative Elements */}
            <div className="absolute -top-6 -right-6 w-32 h-32 bg-teal-200 rounded-full opacity-40 blur-xl" />
            <div className="absolute top-1/2 -right-8 w-24 h-24 bg-amber-200 rounded-full opacity-50 blur-xl" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default StrategicVisionSection;
